"use client";

import { scrollToHashTarget, scrollToHashTargetWhenSettled } from "@v2/helpers/hashScroll";

// The product list is the one section with a sticky toolbar pinned above it, and where
// that toolbar comes to rest depends on whether the site header is open or has folded
// into its top bar. No single scroll margin answers both, so the list is scrolled to
// like any other section first and then lined up against the toolbar as it actually
// sits, once the page has stopped moving and the header has made up its mind.
const LIST_ANCHORS = new Set(["plp_list", "brand-products"]);

const TOOLBAR_ID = "plp_header";

// Under this the list is already where it belongs, and another move would only twitch.
const ALIGN_THRESHOLD = 4;

// A gap this wide means the reader scrolled somewhere else while the page travelled,
// and a page being read is never pulled out from under them.
const HANDOVER_TOLERANCE = 200;

// How long the toolbar is given to take its resting place before it is measured.
const SETTLE_DELAY_MS = 400;

function anchorId(hash: string) {
    return hash.replace(/^#/, "");
}

function isListAnchor(hash: string) {
    return LIST_ANCHORS.has(anchorId(hash));
}

/**
 * Moves the page so the list starts exactly where the toolbar ends. Both are read off
 * the page as it stands, so it is right whether the site header is open or hidden.
 */
function alignListUnderToolbar(hash: string) {
    const list = document.getElementById(anchorId(hash));
    if (!list) return;

    const toolbar = document.getElementById(TOOLBAR_ID);
    const gap = list.getBoundingClientRect().top - (toolbar?.getBoundingClientRect().bottom ?? 0);
    if (Math.abs(gap) <= ALIGN_THRESHOLD || Math.abs(gap) > HANDOVER_TOLERANCE) return;

    window.scrollBy({ top: gap, behavior: "smooth" });
}

/** Runs the alignment once the scroll that came before it has come to rest. */
function alignWhenSettled(hash: string) {
    let done = false;
    let delay = 0;

    const align = () => {
        if (done) return;
        done = true;
        window.removeEventListener("scrollend", align);
        window.clearTimeout(timeout);
        delay = window.setTimeout(() => alignListUnderToolbar(hash), SETTLE_DELAY_MS);
    };

    // Safari has no scrollend event, so the alignment is on a timer there.
    window.addEventListener("scrollend", align);
    const timeout = window.setTimeout(align, 1200);

    return () => {
        done = true;
        window.removeEventListener("scrollend", align);
        window.clearTimeout(timeout);
        window.clearTimeout(delay);
    };
}

/** Scrolls to a section of this page. Returns whether there was one to scroll to. */
export function scrollToPlpSection(hash: string) {
    const found = scrollToHashTarget(hash);
    if (found && isListAnchor(hash)) alignWhenSettled(hash);

    return found;
}

/** The same, for a page that has just been opened and is still settling. */
export function scrollToPlpSectionWhenSettled(hash: string) {
    const cancelScroll = scrollToHashTargetWhenSettled(hash);
    if (!isListAnchor(hash)) return cancelScroll;

    const cancelAlign = alignWhenSettled(hash);
    return () => { cancelScroll(); cancelAlign(); };
}
